import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, MapPin, Calendar, Package, Phone, MessageCircle, Mail, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import VerifiedBadge from '../components/VerifiedBadge';
import VehicleCard from '../components/VehicleCard';
import { maskPhone, maskEmail } from '../utils/contactUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealerProfilePage = () => {
  const { dealerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dealer, setDealer] = useState(null);
  const [hasContactAccess, setHasContactAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDealerProfile();
  }, [dealerId]);

  const fetchDealerProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dealers/profile/${dealerId}`);
      setDealer(response.data);
      
      // Check contact access
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const accessRes = await axios.get(
            `${API}/dealers/${dealerId}/contact-access`,
            {
              headers: { 'Authorization': `Bearer ${token}` },
              withCredentials: true
            }
          );
          setHasContactAccess(accessRes.data.has_access);
        }
      } catch (error) {
        // No auth or no access
        setHasContactAccess(false);
      }
    } catch (error) {
      console.error('Error fetching dealer profile:', error);
      setError(error.response?.data?.detail || 'Failed to load dealer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (dealer?.phone) {
      const message = encodeURIComponent(`Hello, I'm interested in your vehicles on JAMOH & CO AUTOS LTD`);
      window.open(`https://wa.me/${dealer.phone}?text=${message}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !dealer) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass p-8 rounded-sm text-center">
            <Shield className="w-16 h-16 text-textSecondary mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Dealer Not Found</h2>
            <p className="text-textSecondary mb-6">{error || 'The dealer profile you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/marketplace')}
              className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 transition-colors"
            >
              Browse Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Dealer Header */}
        <div className="glass p-8 rounded-sm mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Dealer Info */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-10 h-10 text-primary" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-heading font-bold">{dealer.name}</h1>
                  {dealer.is_verified && (
                    <div className="mt-2">
                      <VerifiedBadge size="md" variant="premium" />
                    </div>
                  )}
                </div>
              </div>

              {/* Dealer Metadata */}
              <div className="flex flex-wrap gap-4 text-sm text-textSecondary">
                {dealer.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{dealer.location}</span>
                  </div>
                )}
                {/* Only show phone/email if user has contact access */}
                {hasContactAccess && dealer.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>{dealer.phone}</span>
                  </div>
                )}
                {hasContactAccess && dealer.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{dealer.email}</span>
                  </div>
                )}
                {dealer.joined_date && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {new Date(dealer.joined_date).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4" />
                  <span>{dealer.active_listings} Active Listing{dealer.active_listings !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* Contact Buttons - Access Controlled */}
            <div className="flex flex-col gap-3">
              {hasContactAccess ? (
                <>
                  {dealer.phone && (
                    <button
                      onClick={handleWhatsAppClick}
                      className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-6 py-3 transition-colors"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Chat on WhatsApp</span>
                    </button>
                  )}
                  {dealer.phone && (
                    <a
                      href={`tel:${dealer.phone}`}
                      className="flex items-center justify-center space-x-2 glass hover:bg-white/10 text-white rounded-sm font-bold px-6 py-3 transition-colors border border-white/20"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Call Dealer</span>
                    </a>
                  )}
                  {dealer.email && (
                    <a
                      href={`mailto:${dealer.email}`}
                      className="flex items-center justify-center space-x-2 glass hover:bg-white/10 text-white rounded-sm font-bold px-6 py-3 transition-colors border border-white/20"
                    >
                      <Mail className="w-5 h-5" />
                      <span>Send Email</span>
                    </a>
                  )}
                </>
              ) : (
                <div className="p-4 bg-yellow-900/20 border border-yellow-600 rounded-sm">
                  <div className="flex items-start space-x-3 mb-3">
                    <Lock className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-400 mb-1">
                        Contact Details Locked
                      </p>
                      <p className="text-xs text-yellow-300">
                        Contact details unlock after successful reservation deposit.
                      </p>
                    </div>
                  </div>
                  {dealer.phone && (
                    <p className="text-xs text-textSecondary text-center mt-2">
                      Phone: {maskPhone(dealer.phone)}
                    </p>
                  )}
                  {dealer.email && (
                    <p className="text-xs text-textSecondary text-center mt-1">
                      Email: {maskEmail(dealer.email)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dealer's Vehicles */}
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold mb-6">Available Vehicles ({dealer.active_listings})</h2>
          
          {dealer.vehicles && dealer.vehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dealer.vehicles.map((vehicle) => (
                <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} />
              ))}
            </div>
          ) : (
            <div className="glass p-12 rounded-sm text-center">
              <Package className="w-16 h-16 text-textSecondary mx-auto mb-4" />
              <p className="text-textSecondary">This dealer currently has no active listings.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealerProfilePage;
